import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PieChartSimpleSkeleton } from './PieChartSimpleSkeleton';
import { Cell, Pie, PieChart as PieChartRecharts } from 'recharts';

import { cn } from '@/utils/tailwind';

import { Tag } from '../../atoms';
import { Card, CardContent } from '../../atoms/Card/Card';

type PieChartSimpleProps = {
  score: number;
  companyScore: number;
  previousScore?: number;
  secondaryText?: string;
  isLoading?: boolean;
  className?: string;
};

const MIN_ANGLE = 14;
const CHART_BG_COLOR = '#F0EEED';

const lightenColor = (hex: string, amount: number = 70): string => {
  const clamp = (val: number) => Math.min(255, Math.max(0, val));

  hex = hex.replace(/^#/, '');

  const num = parseInt(hex, 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;

  const lighten = (channel: number) =>
    clamp(Math.round(channel + ((255 - channel) * amount) / 100));

  const newR = lighten(r);
  const newG = lighten(g);
  const newB = lighten(b);

  return (
    '#' +
    [newR, newG, newB]
      .map(x => x.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase()
  );
};

export const PieChartSimple: React.FC<PieChartSimpleProps> = ({
  score,
  companyScore,
  previousScore,
  secondaryText,
  isLoading,
  className,
}) => {
  const { t } = useTranslation('components');

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  if (isLoading) {
    return (
      <div data-testid="pie-chart-skeleton">
        <PieChartSimpleSkeleton />
      </div>
    );
  }
  const extendedData = [
    {
      id: 1,
      name: 'Main',
      value: score,
      color: '#00897B',
    },
    {
      id: 2,
      name: 'Remaining',
      value: Math.max(0, 100 - score),
      color: CHART_BG_COLOR,
    },
  ];

  const resolvedSecondaryText =
    secondaryText ??
    t('pieChartSimple.previously', {
      companyScore: previousScore ?? companyScore,
    });

  return (
    <Card
      className={cn(
        'px-[65px] py-[50px] border-custom-warm-grey-150 rounded-[12px] w-fit',
        className
      )}
    >
      <CardContent className="flex flex-col items-center justify-center">
        <div>
          <div className="relative mb-5">
            <PieChartRecharts className="relative z-10 shrink-0" width={220} height={220}>
              <Pie
                data={extendedData}
                dataKey="value"
                cx="50%"
                cy="50%"
                innerRadius={88}
                outerRadius={108}
                cornerRadius={10}
                paddingAngle={4}
                startAngle={90}
                endAngle={-270}
                minAngle={MIN_ANGLE}
              >
                {extendedData.map((entry, index) => {
                  const lightColor = lightenColor(entry.color, 30);
                  return (
                    <Cell
                      key={entry.id}
                      fill={entry.color}
                      stroke={hoveredIndex === index ? lightColor : 'transparent'}
                      strokeWidth={4}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      className={cn(
                        'transition-all duration-300 ease-in-out',
                        hoveredIndex !== null && hoveredIndex !== index
                          ? 'opacity-50'
                          : 'opacity-100'
                      )}
                    />
                  );
                })}
              </Pie>
            </PieChartRecharts>
            <div className="absolute flex flex-col items-center justify-center top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <p className="text-body-28-medium !text-custom-warm-grey-800">{score}%</p>

              <div className="flex items-center text-custom-warm-grey-500">
                <p className="text-body-14-regular">{resolvedSecondaryText}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <p className="text-body-16-medium text-custom-warm-grey-500">
              {t('pieChartSimple.companyBaseline')}
            </p>
            <Tag label={`${companyScore}%`} color={score < companyScore ? 'red' : 'cyan'} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
